import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import ValutaFilter from '@eika-infrastruktur/ui-valuta-filter';
import { InfoMessage } from '@eika/attention-message';
import { StandardButton } from '@eika/button';
import { InfoCard, MarketCard } from '@eika/card';
import { CurrencyInput } from '@eika/currency-input';
import { IconTypes } from '@eika/icon';
import { Modal } from '@eika/modal';
import { Select } from '@eika/select';

import { FormTypeCalculatorLoanInfo } from '../../../actions';
import { DispatchWithState, StringKeyAnyValue, StringKeyValue } from '../../../actions/types';
import ErrorMessage from '../../../components/Form/ErrorMessage';
import EquityInfo from '../../../components/LoanCalculator/EquityInfo';
import StepButtons from '../../../components/Steps/StepButtons';
import customHistory from '../../../customHistory';
import { CalculatorReducer } from '../../../reducers/calculator';
import { ContextReducer } from '../../../reducers/context';
import { annuityLoan } from '../../../util/calculator/annuity';
import { getEquityPercentage, getNominalInterest } from '../../../util/calculator/calculator';
import { getEffectiveInterestRate } from '../../../util/calculator/rate';
import { formatPercentage } from '../../../util/common';
import { getProductDescription, getProductName } from '../../../util/mappings';
import LoanProduct from '../../../util/mappings/loanProduct';
import RoutePath from '../../../util/mappings/routePath';
import errorText from '../../../validation/validationTexts';

interface Props {
    action: {
        loanCalculatorNextEvent: (form: FormTypeCalculatorLoanInfo) => DispatchWithState;
    };
    calculator: CalculatorReducer;
    context: ContextReducer;
    form: StringKeyAnyValue;
    text: StringKeyValue;
}

const LoanInfo = ({ action, calculator, context, form, text }: Props): JSX.Element => {
    const [showModal, setShowModal] = useState(false);

    const { loanCalculatorNextEvent } = action;
    const { isLoan, isLoanCommitment, isCar, product, loanInfo, objectAge } = calculator;
    const { loanType, loanProduct } = loanInfo;
    const { interestsAndFees, repaymentPeriodTerms } = product;
    const { loanAmountMin, loanAmountMax, maxAge, establishFee, pledgeFee, termFee } = interestsAndFees;
    const { defTerm } = repaymentPeriodTerms;
    const { appType } = context;
    const {
        formChooseValue,
        formErrorMessage,
        formErrorLoanAmountMessage,
        calculatorLoanInfoLabelVehiclePrice,
        calculatorLoanInfoLabelEquity,
        calculatorLoanInfoAmountTitle,
        calculatorLoanInfoAmountText,
        calculatorLoanInfoApplicationInfoLoanCommitment,
        calculatorLoanInfoRepaymentTitleLoanCommitment,
        calculatorLoanInfoRepaymentLabel,
        calculatorInfoBoxLoanLessThan4Car,
        calculatorInfoBoxLoanLessThan4Other,
        calculatorInfoBoxLoanBetween4And14Car,
        calculatorInfoBoxLoanMoreThan4Other,
        calculatorInfoBoxLoanMoreThan14Car,
        calculatorInfoBoxLoanCommitment,
        calculatorLoanInfoMonthlyCost,
        calculatorLoanInfoTotal,
        calculatorLoanInfoNominalInterestRate,
        calculatorLoanInfoEffectiveInterestRate,
        calculatorLoanInfoEstablishmentFee,
        calculatorLoanInfoRegistrationFee,
        calculatorLoanInfoTermFee,
        calculatorLoanInfoDisclaimer,
        calculatorLoanInfoOpenModal,
        calculatorLoanInfoCloseModal,
        calculatorSubmitButtonLoan,
        calculatorSubmitButtonLoanCommitment,
    } = text;

    const loanAmountErrorText = `${formErrorLoanAmountMessage} ${ValutaFilter(
        loanAmountMin,
        ' ',
        0,
    )},- og ${ValutaFilter(loanAmountMax, ' ', 0)},-`;

    /* Validation Rules (Inline because of dynamic values) */
    const loanInfoSchema = yup.object().shape({
        vehiclePrice: yup
            .number()
            .integer(errorText.vehiclePriceRequired)
            .positive(errorText.vehiclePriceRequired)
            .typeError(errorText.vehiclePriceRequired)
            .min(0, errorText.vehiclePriceRequired)
            .required(errorText.vehiclePriceRequired),

        equity: yup
            .number()
            .integer(errorText.equityRequired)
            .typeError(errorText.equityRequired)
            .min(0, errorText.equityMin)
            .required(errorText.equityRequired),

        loanAmountWithoutFees: yup
            .number()
            .required(loanAmountErrorText)
            .integer(loanAmountErrorText)
            .min(loanAmountMin, loanAmountErrorText)
            .max(loanAmountMax, loanAmountErrorText),

        requestedLoanPeriodInMonths: yup
            .number()
            .integer(errorText.loanDurationRequired)
            .positive(errorText.loanDurationRequired)
            .typeError(errorText.loanDurationRequired)
            .required(errorText.loanDurationRequired),
    });

    const {
        handleSubmit,
        register,
        formState: { errors },
        watch,
        setValue,
    } = useForm<FormTypeCalculatorLoanInfo>({
        resolver: yupResolver(loanInfoSchema),
        mode: 'all',
        defaultValues: {
            vehiclePrice: 0,
            equity: 0,
            loanAmountWithoutFees: 0,
            requestedLoanPeriodInMonths: defTerm,
        },
    });

    const [vehiclePrice, equity, loanAmountWithoutFees, requestedLoanPeriodInMonths] = watch([
        'vehiclePrice',
        'equity',
        'loanAmountWithoutFees',
        'requestedLoanPeriodInMonths',
    ]);
    const vehiclePriceNumber = Number(vehiclePrice);
    const equityNumber = Number(equity);

    /* Updates the Loan Amount based on input from price & equity */
    useEffect(() => {
        if (vehiclePriceNumber > 0 && equityNumber >= 0)
            setValue('loanAmountWithoutFees', vehiclePriceNumber - equityNumber, { shouldValidate: true });
    }, [setValue, vehiclePriceNumber, equityNumber]);

    /* Form Errors */
    const hasError = Object.keys(errors).length > 0;
    const formError = <ErrorMessage message={formErrorMessage} />;

    /* Calculate Repayment Period */
    const maxTerms = maxAge ? maxAge - objectAge : 10;

    let repaymentPeriod =
        isLoanCommitment || maxTerms >= 10
            ? form.requestedLoanPeriodInMonths.values.slice(0, 10)
            : form.requestedLoanPeriodInMonths.values.slice(0, maxTerms);

    if (loanProduct === LoanProduct.carNoInsurance) {
        repaymentPeriod = form.requestedLoanPeriodInMonths.values.slice(0, 5);
    }

    /* Get Equity Percentage */
    const equityPercentage = getEquityPercentage(equityNumber, vehiclePriceNumber);

    /* Get Nominal Interest Rate */
    const loanInfoNominalInterestRate = getNominalInterest(equityNumber, vehiclePriceNumber, interestsAndFees);

    /* Loan Info Texts */
    const loanInfoTitle = getProductName(loanType, loanProduct);
    const loanInfoDescription = getProductDescription(loanProduct, text);

    const monthlyCost = annuityLoan(
        loanInfoNominalInterestRate,
        establishFee,
        pledgeFee,
        termFee,
        loanAmountWithoutFees,
        requestedLoanPeriodInMonths,
    );
    const loanInfoTotal =
        monthlyCost && requestedLoanPeriodInMonths ? monthlyCost * requestedLoanPeriodInMonths : undefined;
    const present = loanAmountWithoutFees && pledgeFee ? loanAmountWithoutFees + pledgeFee : undefined;
    const loanInfoEffectiveInterestRate = getEffectiveInterestRate(requestedLoanPeriodInMonths, monthlyCost, present);

    return (
        <div className="loan-info">
            <form
                onSubmit={handleSubmit((form) => {
                    loanCalculatorNextEvent(form);
                })}
            >
                <div className="loan-info__vehicle-price input-wrapper">
                    <CurrencyInput
                        {...register('vehiclePrice')}
                        label={calculatorLoanInfoLabelVehiclePrice}
                        placeholder={text.currencyInputPlaceholderKr}
                        error={errors?.vehiclePrice?.message || ''}
                        maxLength={12}
                        min={30000}
                        max={2000000}
                        id="calculatorBuySum"
                    />
                </div>

                <div className="loan-info__equity input-wrapper">
                    <CurrencyInput
                        {...register('equity')}
                        label={calculatorLoanInfoLabelEquity}
                        placeholder={text.currencyInputPlaceholderKr}
                        error={errors?.equity?.message || ''}
                        maxLength={12}
                        min={0}
                        id="calculatorEquity"
                    />
                    {!!(vehiclePrice && equityPercentage >= 1 && equityPercentage < 100) &&
                        loanProduct !== LoanProduct.carNoInsurance && (
                            <EquityInfo
                                className="loan-info__equity-label"
                                text={text}
                                equity={equity}
                                price={vehiclePrice}
                            />
                        )}
                </div>

                <input {...register('loanAmountWithoutFees')} type="hidden" defaultValue={loanAmountWithoutFees} />

                {errors?.loanAmountWithoutFees && <ErrorMessage message={errors.loanAmountWithoutFees.message} />}

                {!!(loanAmountWithoutFees && loanAmountWithoutFees > 0) && (
                    <InfoCard className="loan-info__amount">
                        <div className="loan-info__amount-title">
                            <h3>{calculatorLoanInfoAmountTitle}</h3>
                        </div>
                        <div className="loan-info__amount-price">{ValutaFilter(loanAmountWithoutFees, ' ', 0)} kr</div>
                        {isLoanCommitment && (
                            <div className="loan-info__amount-info">{calculatorLoanInfoAmountText}</div>
                        )}
                    </InfoCard>
                )}

                {isLoanCommitment && (
                    <div className="loan-info__application-info">
                        <p>{calculatorLoanInfoApplicationInfoLoanCommitment}</p>
                    </div>
                )}

                <div className="loan-info__repayment-period input-wrapper">
                    {isLoanCommitment && <h2>{calculatorLoanInfoRepaymentTitleLoanCommitment}</h2>}

                    <Select
                        {...register('requestedLoanPeriodInMonths', {
                            valueAsNumber: true,
                        })}
                        label={calculatorLoanInfoRepaymentLabel}
                        defaultValue={loanInfo.requestedLoanPeriodInMonths || 'choose'}
                        error={errors?.requestedLoanPeriodInMonths?.message || ''}
                        id="calculatorRepaymentTime"
                    >
                        <option value="choose" disabled>
                            {formChooseValue}
                        </option>

                        {repaymentPeriod.map((d: { text: string; value: number }, index: number) => {
                            const id = `options_${index}`;
                            return (
                                <option value={d.value} key={id}>
                                    {d.text}
                                </option>
                            );
                        })}
                    </Select>
                </div>

                {isLoan && (
                    <>
                        {objectAge < 5 && (
                            <InfoMessage
                                className="loan-info__info-message"
                                icon={IconTypes.LYSPAERE_24}
                                text={isCar ? calculatorInfoBoxLoanLessThan4Car : calculatorInfoBoxLoanLessThan4Other}
                            />
                        )}
                        {objectAge > 4 && objectAge < 14 && (
                            <InfoMessage
                                className="loan-info__info-message"
                                icon={IconTypes.LYSPAERE_24}
                                text={
                                    isCar ? calculatorInfoBoxLoanBetween4And14Car : calculatorInfoBoxLoanMoreThan4Other
                                }
                            />
                        )}
                        {objectAge > 13 && (
                            <InfoMessage
                                className="loan-info__info-message"
                                icon={IconTypes.LYSPAERE_24}
                                text={isCar ? calculatorInfoBoxLoanMoreThan14Car : calculatorInfoBoxLoanMoreThan4Other}
                            />
                        )}
                    </>
                )}

                {isLoanCommitment && (
                    <InfoMessage
                        className="loan-info__info-message"
                        icon={IconTypes.LYSPAERE_24}
                        text={calculatorInfoBoxLoanCommitment}
                    />
                )}

                {loanAmountWithoutFees !== undefined &&
                    equity !== undefined &&
                    loanAmountWithoutFees > 0 &&
                    equity >= 0 &&
                    typeof requestedLoanPeriodInMonths === 'number' && (
                        <div className="loan-info__product-info">
                            <MarketCard
                                className="loan-info__product-info-card"
                                secondaryButton={
                                    <MarketCard.SecondaryButton onClick={() => setShowModal(!showModal)}>
                                        {calculatorLoanInfoOpenModal}
                                    </MarketCard.SecondaryButton>
                                }
                            >
                                {loanInfoTitle && <h1>{loanInfoTitle}</h1>}

                                <div className="custom-list">
                                    {monthlyCost && (
                                        <ul>
                                            <li className="list-left">{calculatorLoanInfoMonthlyCost}</li>
                                            <li className="list-right">{ValutaFilter(monthlyCost, ' ', 0)} kr</li>
                                        </ul>
                                    )}

                                    {loanInfoNominalInterestRate && (
                                        <ul>
                                            <li className="list-left">{calculatorLoanInfoNominalInterestRate}</li>
                                            <li className="list-right">
                                                {formatPercentage(loanInfoNominalInterestRate)} %
                                            </li>
                                        </ul>
                                    )}
                                </div>

                                <Modal
                                    id="modal"
                                    title={loanInfoTitle}
                                    onClose={() => setShowModal(false)}
                                    show={showModal}
                                    appNamespace="no-eika-securedloan"
                                >
                                    <div className="custom-list">
                                        {monthlyCost && (
                                            <ul>
                                                <li className="list-left">{calculatorLoanInfoMonthlyCost}</li>
                                                <li className="list-right">{ValutaFilter(monthlyCost, ' ', 0)} kr</li>
                                            </ul>
                                        )}

                                        {!!loanInfoTotal && (
                                            <ul style={{ marginBottom: '40px' }}>
                                                <li className="list-left">{calculatorLoanInfoTotal}</li>
                                                <li className="list-right">{ValutaFilter(loanInfoTotal, ' ', 0)} kr</li>
                                            </ul>
                                        )}

                                        {loanInfoNominalInterestRate && (
                                            <ul>
                                                <li className="list-left">{calculatorLoanInfoNominalInterestRate}</li>
                                                <li className="list-right">
                                                    {formatPercentage(loanInfoNominalInterestRate)} %
                                                </li>
                                            </ul>
                                        )}
                                        {loanInfoEffectiveInterestRate && (
                                            <ul style={{ marginBottom: '40px' }}>
                                                <li className="list-left">{calculatorLoanInfoEffectiveInterestRate}</li>
                                                <li className="list-right">
                                                    {formatPercentage(loanInfoEffectiveInterestRate)} %
                                                </li>
                                            </ul>
                                        )}

                                        {establishFee && (
                                            <ul>
                                                <li className="list-left">{calculatorLoanInfoEstablishmentFee}</li>
                                                <li className="list-right">{ValutaFilter(establishFee, ' ', 0)} kr</li>
                                            </ul>
                                        )}
                                        {pledgeFee && (
                                            <ul>
                                                <li className="list-left">{calculatorLoanInfoRegistrationFee}</li>
                                                <li className="list-right">{ValutaFilter(pledgeFee, ' ', 0)} kr</li>
                                            </ul>
                                        )}
                                        {termFee && (
                                            <ul style={{ marginBottom: '40px' }}>
                                                <li className="list-left">{calculatorLoanInfoTermFee}</li>
                                                <li className="list-right">{ValutaFilter(termFee, ' ', 0)} kr</li>
                                            </ul>
                                        )}
                                    </div>

                                    {(loanInfoDescription || calculatorLoanInfoDisclaimer) && (
                                        <div className="loan-info__description">
                                            {loanInfoDescription && <p>{loanInfoDescription}</p>}
                                            {calculatorLoanInfoDisclaimer && <p>{calculatorLoanInfoDisclaimer}</p>}
                                        </div>
                                    )}

                                    {calculatorLoanInfoCloseModal && (
                                        <div className="loan-info__close-modal">
                                            <StandardButton onClick={() => setShowModal(false)}>
                                                {calculatorLoanInfoCloseModal}
                                            </StandardButton>
                                        </div>
                                    )}
                                </Modal>
                            </MarketCard>
                        </div>
                    )}

                {hasError && formError}

                {/* Application Button */}
                <StepButtons
                    showPrevious
                    nextButtonName={isLoan ? calculatorSubmitButtonLoan : calculatorSubmitButtonLoanCommitment}
                    nextButtonType="submit"
                    previousChangeEvent={() =>
                        appType === 'calculator' ? customHistory.push('/') : customHistory.push(RoutePath.calculator)
                    }
                />
            </form>
        </div>
    );
};

export default LoanInfo;
